import {
  ENTITY_TYPE,
  IFilterEntity,
  InitialFilterValues,
  SupplierInitialFilterValues,
  InitialSearchByValues
} from '../../constants'
import { CustomerProfileBulkUpdateEntity, IDashboardState } from './dashboard.types'

// v4: matched the legacy Android sort menu (supplier drops Latest, both tabs default
// to "Default", customer order changed). Bump to drop stale persisted customer filters.
const SORT_FILTERS_STORAGE_KEY = 'okc_customer_supplier_sort_filters_v5'
// Suppliers persist their sort separately from customers so the two tabs never
// clobber each other's selection.
const SUPPLIER_SORT_FILTERS_STORAGE_KEY = 'okc_supplier_sort_filters_v1'

/**
 * Load a previously-selected sort/filter preference from localStorage, or fall
 * back to `fallback`. Failures are non-fatal — just return the fallback.
 */
const loadPersistedFilters = (key: string, fallback: IFilterEntity[]): IFilterEntity[] => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return fallback
  }
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) {
      return fallback
    }
    const parsed = JSON.parse(raw) as IFilterEntity[]
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return fallback
    }
    return parsed
  } catch {
    return fallback
  }
}

const persistFilters = (key: string, filters: IFilterEntity[]): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(filters))
  } catch {
    // Storage full / disabled — ignore; sort still works for the session.
  }
}

const loadInitialFilters = (): IFilterEntity[] =>
  loadPersistedFilters(SORT_FILTERS_STORAGE_KEY, InitialFilterValues)

const loadInitialSupplierFilters = (): IFilterEntity[] =>
  loadPersistedFilters(SUPPLIER_SORT_FILTERS_STORAGE_KEY, SupplierInitialFilterValues)

export const persistSortFilters = (filters: IFilterEntity[]): void =>
  persistFilters(SORT_FILTERS_STORAGE_KEY, filters)

// The store hydrates from the server's __PRELOADED_STATE__, where `window` is undefined so
// both filter sets fall back to Default. Read the persisted sort on the client (null when
// nothing was saved) so the restore effect can re-apply it over that SSR default.
//
// Customers and suppliers deliberately read the SAME key their reducer writes. An earlier
// version had customers restoring from a second, separate key (utils/ledgerFilterStorage),
// written only from the filter sheet's onSave. Any saveFilters dispatch that didn't originate
// there — CLEAR_FILTERS being the obvious one — updated this key but not that one, so on the
// next reload the restore effect replayed the stale value and undid the change.
const readPersisted = (key: string): IFilterEntity[] | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as IFilterEntity[]
    return Array.isArray(parsed) && parsed.length ? parsed : null
  } catch {
    return null
  }
}

export const getPersistedSortFilters = (): IFilterEntity[] | null =>
  readPersisted(SORT_FILTERS_STORAGE_KEY)

export const getPersistedSupplierFilters = (): IFilterEntity[] | null =>
  readPersisted(SUPPLIER_SORT_FILTERS_STORAGE_KEY)

export const persistSupplierSortFilters = (filters: IFilterEntity[]): void =>
  persistFilters(SUPPLIER_SORT_FILTERS_STORAGE_KEY, filters)
const NET_BALANCE_HIDDEN_STORAGE_KEY = 'okc_net_balance_hidden_v1'

export interface INetBalanceHidden {
  customer: boolean
  supplier: boolean
}

const NET_BALANCE_HIDDEN_DEFAULT: INetBalanceHidden = { customer: true, supplier: true }

export const getPersistedNetBalanceHidden = (): INetBalanceHidden => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return NET_BALANCE_HIDDEN_DEFAULT
  }
  try {
    const raw = window.localStorage.getItem(NET_BALANCE_HIDDEN_STORAGE_KEY)
    if (!raw) {
      return NET_BALANCE_HIDDEN_DEFAULT
    }
    const parsed = JSON.parse(raw) as Partial<INetBalanceHidden>
    return { customer: parsed.customer !== false, supplier: parsed.supplier !== false }
  } catch {
    return NET_BALANCE_HIDDEN_DEFAULT
  }
}

export const persistNetBalanceHidden = (value: INetBalanceHidden): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    window.localStorage.setItem(NET_BALANCE_HIDDEN_STORAGE_KEY, JSON.stringify(value))
  } catch {}
}

export const DashboardState: IDashboardState = {
  entityAction: {
    loading: false
  },
  brandData: {},
  businessConfigWithBusinessId: {},
  customers: {
    byIds: {},
    allIds: [],
    loading: false
  },
  customerLastActivity: {},
  supplierLastActivity: {},
  // Suppliers sort independently of customers; their selection persists across
  // reloads via its own storage key (Android parity).
  supplierFilters: loadInitialSupplierFilters(),
  suppliers: {
    byIds: {},
    allIds: [],
    loading: false
  },
  businessSettings: {},
  merchantRegistration: {
    loading: false,
    show: false,
    optedOut: false
  },
  merchantProfile: {
    loading: false
  },
  businessTypes: [],
  businessCategories: [],
  filters: loadInitialFilters(),
  currentTab: ENTITY_TYPE.CUSTOMER,
  searchString: '',
  phonebookContacts: {
    loading: false,
    data: []
  },
  helpSections: {
    loading: false,
    data: []
  },
  currentCustomer: null,
  showLeadgenBanner: false,
  txnAlertNudge: null,
  pendingTxnAlertUpgrade: null,
  customersByTags: undefined,
  tagsFilter: InitialFilterValues,
  searchFilters: InitialSearchByValues,
  billsFiltered: undefined,
  businessAccounts: {
    businessIds: [],
    asStaffBusinessIds: [],
    loading: false,
    drawers: {
      newBusinessName: false
    }
  },
  accountsDocument: null,
  loaders: {
    bulkUploadCustomers: false,
    downloadCustomerProfiles: false
  },
  paidSoonBanner: {
    isShow: false,
    feedback: '',
    isDone: false
  },
  businessManagers: [],
  isBusinessManagerActionInProgress: false,
  customerProfiles: {
    customers: [],
    paginationData: {
      page: 1,
      per_page: 50,
      total_items: 0
    },
    resetSelectedRowRef: {
      customersTable: null
    },
    drawers: {
      [CustomerProfileBulkUpdateEntity.tag]: {
        status: false,
        replace: false
      },
      [CustomerProfileBulkUpdateEntity.beat]: {
        status: false,
        replace: false
      },
      [CustomerProfileBulkUpdateEntity.route]: {
        status: false,
        replace: false
      }
    }
  }
}
